import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useUiLanguage } from "../i18n";
import { SearchIcon } from "./icons";

export interface SearchSelectOption {
    readonly value: string;
    readonly label: string;
    readonly description?: string;
}

interface SearchSelectProps {
    readonly ariaLabel: string;
    readonly value: string;
    readonly options: readonly SearchSelectOption[];
    readonly onChange: (value: string) => void;
    readonly searchPlaceholder?: string;
    readonly compact?: boolean;
}

export function SearchSelect({
    ariaLabel,
    value,
    options,
    onChange,
    searchPlaceholder,
    compact = false,
}: SearchSelectProps): React.JSX.Element {
    const { t } = useUiLanguage();
    const resolvedSearchPlaceholder = searchPlaceholder ?? t("searchComponents");
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const rootRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();
    const selected = options.find((option) => option.value === value);
    const filteredOptions = useMemo(() => {
        const normalized = query.trim().toLowerCase();
        if (normalized === "") {
            return options;
        }
        return options.filter((option) =>
            `${option.label} ${option.description ?? ""}`.toLowerCase().includes(normalized),
        );
    }, [options, query]);

    useEffect(() => {
        function closeOnOutsideClick(event: MouseEvent): void {
            if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
                setOpen(false);
                setQuery("");
            }
        }
        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    useEffect(() => {
        if (open) {
            searchRef.current?.focus();
        }
    }, [open]);

    function choose(option: SearchSelectOption): void {
        onChange(option.value);
        setOpen(false);
        setQuery("");
        setActiveIndex(0);
    }

    function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
        if (event.key === "Escape") {
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (filteredOptions.length > 0) {
                setActiveIndex((index) => Math.min(filteredOptions.length - 1, index + 1));
            }
            return;
        }
        if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(0, index - 1));
            return;
        }
        if (event.key === "Enter") {
            const option = filteredOptions[activeIndex];
            if (option !== undefined) {
                event.preventDefault();
                choose(option);
            }
        }
    }

    return (
        <div
            ref={rootRef}
            className={`ui-select search-select${open ? " is-open" : ""}${compact ? " is-compact" : ""}`}
        >
            <button
                className="ui-select-trigger"
                type="button"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-controls={listboxId}
                aria-expanded={open}
                onClick={() => setOpen((valueOpen) => !valueOpen)}
                onKeyDown={(event) => {
                    if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                <span>{selected?.label ?? t("select")}</span>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path d="m4 6 4 4 4-4" />
                </svg>
            </button>
            {open ? (
                <div className="ui-select-menu">
                    <label className="ui-select-search">
                        <SearchIcon />
                        <span className="sr-only">{resolvedSearchPlaceholder}</span>
                        <input
                            ref={searchRef}
                            type="search"
                            value={query}
                            onChange={(event) => {
                                setQuery(event.target.value);
                                setActiveIndex(0);
                            }}
                            onKeyDown={handleSearchKeyDown}
                            placeholder={resolvedSearchPlaceholder}
                            autoComplete="off"
                        />
                    </label>
                    <div
                        id={listboxId}
                        className="ui-select-options scroll-region"
                        role="listbox"
                        aria-label={ariaLabel}
                    >
                        {filteredOptions.map((option, index) => (
                            <button
                                key={option.value}
                                type="button"
                                role="option"
                                aria-selected={option.value === value}
                                className={`ui-select-option${option.value === value ? " is-selected" : ""}${index === activeIndex ? " is-active" : ""}`}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => choose(option)}
                            >
                                <span>{option.label}</span>
                                {option.description === undefined ? null : <small>{option.description}</small>}
                            </button>
                        ))}
                        {filteredOptions.length === 0 ? (
                            <div className="ui-select-empty">{t("noMatchingOptions")}</div>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
